import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const formatMoney = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v);
};

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicKey = (u) => `${u?.areaId || 'na'}-${u?.organizadorId || 'null'}-${u?.ubicacionId || 'null'}`;

/* El mismo producto puede aparecer como línea suelta y, a la vez, como componente de uno o
   varios paquetes dentro de la misma cotización — ambos casos comparten el mismo inventario
   real. `pool` se pasa por referencia y se va descontando a medida que se asigna, para que
   ninguna combinación producto+ubicación se reserve dos veces entre las distintas tablas. */
const autoAsignar = (productoId, cantidad, ubicaciones, pool) => {
    const getPoolKey = (u) => `${productoId}__${buildUbicKey(u)}`;
    const lista = Array.isArray(ubicaciones) ? ubicaciones : [];

    lista.forEach((u) => {
        const k = getPoolKey(u);
        if (!(k in pool)) pool[k] = Number(u.disponible || 0);
    });

    const ordenadas = [...lista].sort((a, b) => pool[getPoolKey(b)] - pool[getPoolKey(a)]);
    const asignacion = {};
    let restante = cantidad;

    ordenadas.forEach((u) => {
        if (restante <= 0) return;
        const k = getPoolKey(u);
        const disponible = pool[k] || 0;
        const usar = Math.min(restante, disponible);
        if (usar > 0) {
            asignacion[buildUbicKey(u)] = usar;
            restante -= usar;
            pool[k] = disponible - usar;
        }
    });

    return asignacion;
};

/* Convierte una cotización completa en venta: todo o nada. No existe un modo parcial — para
   poder confirmar, cada producto (excepto los marcados sinInventario) debe quedar 100% asignado
   a una o varias ubicaciones reales de inventario; si a algún producto le falta stock, el botón
   de confirmar permanece deshabilitado hasta que se ajuste la cotización o haya inventario. */
export default function ConvertirVentaModal({ show, onHide, onConfirm, productos, paquetes, clienteLabel, idCliente, metodosOptions, monedaDefault, tipoCambioDefault, loading }) {
    const [metodoSel, setMetodoSel] = useState(null);
    const [cobroInmediato, setCobroInmediato] = useState(true);
    const [fecha, setFecha] = useState(new Date());
    const [asignaciones, setAsignaciones] = useState({});
    const [asignacionesPaquete, setAsignacionesPaquete] = useState({});
    const [moneda, setMoneda] = useState(0);
    const [tipoCambioUsd, setTipoCambioUsd] = useState('1');

    /* Sin cliente, el cobro siempre es en el momento (una venta a crédito requiere poder cobrarle a alguien) */
    const esCobradaInmediato = !idCliente || cobroInmediato;

    const getTc = () => {
        const tc = Number(tipoCambioUsd);
        return Number.isFinite(tc) && tc > 0 ? tc : 1;
    };

    const validarTipoCambioSiUSD = () => {
        if (Number(moneda) !== 1) return true;
        const tc = Number(tipoCambioUsd);
        return Number.isFinite(tc) && tc > 0;
    };

    const prefMoneda = Number(moneda) === 1 ? 'USD $' : '$';

    const menuPortalTarget = useMemo(() => {
        if (typeof document === 'undefined') return null;
        return document.body;
    }, []);

    const listado = useMemo(() => (Array.isArray(productos) ? productos : []), [productos]);
    const listadoPaquetes = useMemo(() => (Array.isArray(paquetes) ? paquetes : []), [paquetes]);

    useEffect(() => {
        if (!show) return;

        setMetodoSel(null);
        setCobroInmediato(true);
        setFecha(new Date());
        setMoneda(Number(monedaDefault) === 1 ? 1 : 0);
        setTipoCambioUsd(tipoCambioDefault ? String(tipoCambioDefault) : '1');

        const poolDisponible = {};

        const init = {};
        listado.forEach((it) => {
            if (it.sinInventario) return;
            const cantidad = Number(it.cantidad || 0);
            init[it.key] = autoAsignar(it.productoId, cantidad, it.ubicacionesDisponibles, poolDisponible);
        });
        setAsignaciones(init);

        const initPaquetes = {};
        listadoPaquetes.forEach((p) => {
            const cantidadPaquetes = Number(p.cantidad || 0);
            (Array.isArray(p.componentes) ? p.componentes : []).forEach((c) => {
                const conversion = Number(c?.conversion || 1) || 1;
                const necesaria = (Number(c?.cantidadPorPaquete || 0) * cantidadPaquetes) / conversion;
                const opciones = Array.isArray(c?.ubicacionesDisponibles) ? c.ubicacionesDisponibles : [];
                initPaquetes[`${p.key}__${c.productoId}`] = autoAsignar(c.productoId, necesaria, opciones, poolDisponible);
            });
        });
        setAsignacionesPaquete(initPaquetes);
    }, [show, listado, listadoPaquetes, monedaDefault, tipoCambioDefault]);

    const total = useMemo(() => {
        const totalProductos = listado.reduce((acc, it) => {
            const precio = Number(it?.precio || 0);
            const cantidad = Number(it?.cantidad || 0);
            const base = precio * cantidad;
            return acc + (it.iva ? base * 1.16 : base);
        }, 0);

        const totalPaquetes = listadoPaquetes.reduce((acc, it) => {
            const precio = Number(it?.precio || 0);
            const cantidad = Number(it?.cantidad || 0);
            const base = precio * cantidad;
            return acc + (it.iva ? base * 1.16 : base);
        }, 0);

        return totalProductos + totalPaquetes;
    }, [listado, listadoPaquetes]);

    const estadoPorPaquete = useMemo(() => {
        const map = {};

        listadoPaquetes.forEach((p) => {
            const cantidadPaquetes = Number(p.cantidad || 0);
            const componentes = Array.isArray(p.componentes) ? p.componentes : [];

            const detalleComponentes = componentes.map((c) => {
                const conversion = Number(c?.conversion || 1) || 1;
                const necesaria = (Number(c?.cantidadPorPaquete || 0) * cantidadPaquetes) / conversion;
                const ubicaciones = Array.isArray(c?.ubicacionesDisponibles) ? c.ubicacionesDisponibles : [];
                const asign = asignacionesPaquete[`${p.key}__${c.productoId}`] || {};
                const asignado = Object.values(asign).reduce((acc, v) => acc + Number(v || 0), 0);
                const disponibleTotal = ubicaciones.reduce((acc, u) => acc + Number(u.disponible || 0), 0);

                return {
                    productoId: c.productoId,
                    productoNombre: c.productoNombre,
                    necesaria,
                    ubicaciones,
                    asign,
                    asignado,
                    disponibleTotal,
                    completo: asignado === necesaria,
                    sinStock: disponibleTotal < necesaria
                };
            });

            map[p.key] = {
                detalleComponentes,
                completo: detalleComponentes.length > 0 && detalleComponentes.every((d) => d.completo && !d.sinStock)
            };
        });

        return map;
    }, [listadoPaquetes, asignacionesPaquete]);

    const estadoPorProducto = useMemo(() => {
        const map = {};

        listado.forEach((it) => {
            if (it.sinInventario) {
                map[it.key] = { ubicaciones: [], asignado: 0, disponibleTotal: 0, completo: true, sinStock: false };
                return;
            }

            const ubicaciones = Array.isArray(it.ubicacionesDisponibles) ? it.ubicacionesDisponibles : [];
            const cantidad = Number(it.cantidad || 0);
            const asign = asignaciones[it.key] || {};
            const asignado = Object.values(asign).reduce((acc, v) => acc + Number(v || 0), 0);
            const disponibleTotal = ubicaciones.reduce((acc, u) => acc + Number(u.disponible || 0), 0);

            map[it.key] = {
                ubicaciones,
                asignado,
                disponibleTotal,
                completo: asignado === cantidad,
                sinStock: disponibleTotal < cantidad
            };
        });

        return map;
    }, [listado, asignaciones]);

    /* Demanda total ya reservada por producto+ubicación, sumando tanto las líneas sueltas como
       los componentes de todos los paquetes — es el mismo inventario real, así que lo que una
       tabla ya reservó no puede volver a ofrecerse como disponible en la otra. */
    const asignadoGlobalPorProductoUbic = useMemo(() => {
        const map = {};
        const addKey = (productoId, ubicKey, val) => {
            const k = `${productoId}__${ubicKey}`;
            map[k] = (map[k] || 0) + Number(val || 0);
        };

        listado.forEach((it) => {
            if (it.sinInventario) return;
            const asign = asignaciones[it.key] || {};
            Object.entries(asign).forEach(([ubicKey, val]) => addKey(it.productoId, ubicKey, val));
        });

        listadoPaquetes.forEach((p) => {
            (Array.isArray(p.componentes) ? p.componentes : []).forEach((c) => {
                const asign = asignacionesPaquete[`${p.key}__${c.productoId}`] || {};
                Object.entries(asign).forEach(([ubicKey, val]) => addKey(c.productoId, ubicKey, val));
            });
        });

        return map;
    }, [listado, listadoPaquetes, asignaciones, asignacionesPaquete]);

    const disponibleRestante = (productoId, ubic, propio) => {
        const k = `${productoId}__${buildUbicKey(ubic)}`;
        const otros = Number(asignadoGlobalPorProductoUbic[k] || 0) - Number(propio || 0);
        return Math.max(0, Number(ubic.disponible || 0) - otros);
    };

    const onChangeAsignacion = (itemKey, productoId, ubic, rawValue) => {
        const v = rawValue === '' ? 0 : Number(rawValue);
        const propio = Number((asignaciones[itemKey] || {})[buildUbicKey(ubic)] || 0);
        const clamped = Math.max(0, Math.min(v, disponibleRestante(productoId, ubic, propio)));

        setAsignaciones((prev) => ({
            ...prev,
            [itemKey]: {
                ...(prev[itemKey] || {}),
                [buildUbicKey(ubic)]: clamped
            }
        }));
    };

    const onChangeAsignacionPaquete = (paqueteKey, productoId, ubic, rawValue) => {
        const v = rawValue === '' ? 0 : Number(rawValue);
        const key = `${paqueteKey}__${productoId}`;
        const propio = Number((asignacionesPaquete[key] || {})[buildUbicKey(ubic)] || 0);
        const clamped = Math.max(0, Math.min(v, disponibleRestante(productoId, ubic, propio)));

        setAsignacionesPaquete((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                [buildUbicKey(ubic)]: clamped
            }
        }));
    };

    const hayFaltantes = listado.some((it) => !estadoPorProducto[it.key]?.completo || estadoPorProducto[it.key]?.sinStock);
    const hayFaltantesPaquetes = listadoPaquetes.some((p) => !estadoPorPaquete[p.key]?.completo);

    const puedeConfirmar = (!esCobradaInmediato || !!metodoSel?.value)
        && (listado.length > 0 || listadoPaquetes.length > 0)
        && !hayFaltantes
        && !hayFaltantesPaquetes
        && validarTipoCambioSiUSD();

    const handleConfirm = () => {
        if (!puedeConfirmar) return;

        const lineas = [];

        listado.forEach((it) => {
            if (it.sinInventario) {
                lineas.push({
                    productoId: it.productoId,
                    unidadId: it.unidadId || null,
                    cantidad: Number(it.cantidad || 0),
                    precio: Number(it.precio || 0),
                    conversion: Number(it.conversion || 1),
                    areaId: null,
                    organizadorId: null,
                    ubicacionId: null,
                    iva: it.iva || false
                });
                return;
            }

            const asign = asignaciones[it.key] || {};
            const ubicaciones = Array.isArray(it.ubicacionesDisponibles) ? it.ubicacionesDisponibles : [];

            ubicaciones.forEach((u) => {
                const cant = Number(asign[buildUbicKey(u)] || 0);
                if (cant <= 0) return;

                lineas.push({
                    productoId: it.productoId,
                    unidadId: it.unidadId || null,
                    cantidad: cant,
                    precio: Number(it.precio || 0),
                    conversion: Number(it.conversion || 1),
                    areaId: u.areaId || null,
                    organizadorId: u.organizadorId || null,
                    ubicacionId: u.ubicacionId || null,
                    iva: it.iva || false
                });
            });
        });

        const lineasPaquetes = listadoPaquetes.map((p) => {
            const componentes = Array.isArray(p.componentes) ? p.componentes : [];

            return {
                paqueteId: p.paqueteId,
                cantidad: Number(p.cantidad || 0),
                precio: Number(p.precio || 0),
                iva: p.iva || false,
                componentes: componentes.flatMap((c) => {
                    const ubicaciones = Array.isArray(c.ubicacionesDisponibles) ? c.ubicacionesDisponibles : [];
                    const asign = asignacionesPaquete[`${p.key}__${c.productoId}`] || {};

                    return ubicaciones
                        .filter((u) => Number(asign[buildUbicKey(u)] || 0) > 0)
                        .map((u) => ({
                            productoId: c.productoId,
                            cantidad: Number(asign[buildUbicKey(u)]),
                            areaId: u.areaId || null,
                            organizadorId: u.organizadorId || null,
                            ubicacionId: u.ubicacionId || null
                        }));
                })
            };
        });

        onConfirm({
            id_metodo: esCobradaInmediato ? metodoSel.value : null,
            cobro_inmediato: esCobradaInmediato,
            moneda: Number(moneda) === 1 ? 1 : 0,
            tipo_cambio: Number(moneda) === 1 ? getTc() : 1,
            fecha,
            lineas,
            lineasPaquetes
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Convertir cotización en venta</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ overflow: 'visible' }}>
                <div className="alert alert-warning" style={{ fontSize: 13 }}>
                    <b>Importante:</b> la conversión es todo o nada — debe cubrirse el 100% de cada producto y de cada
                    componente de los paquetes con inventario real disponible. Si falta stock de algo, no se podrá
                    confirmar hasta que se ajuste la cotización o haya inventario suficiente.
                </div>

                <div className="mb-3">
                    <div className="text-muted" style={{ fontSize: 12 }}>Cliente</div>
                    <div className="fw-semibold">{clienteLabel || 'Sin cliente'}</div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-4">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                            Método de pago{esCobradaInmediato ? '' : ' (opcional)'}
                        </div>
                        <Select
                            placeholder="Selecciona método"
                            isClearable
                            isDisabled={!esCobradaInmediato}
                            value={metodoSel}
                            onChange={(opt) => setMetodoSel(opt || null)}
                            options={metodosOptions}
                            menuPortalTarget={menuPortalTarget}
                            menuPosition="fixed"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            noOptionsMessage={() => 'Sin métodos'}
                        />
                    </div>

                    <div className="col-md-2">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>&nbsp;</div>
                        <Form.Check
                            type="checkbox"
                            id="convertir-venta-cobro-inmediato"
                            label="Cobro inmediato"
                            checked={esCobradaInmediato}
                            disabled={!idCliente}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setCobroInmediato(checked);
                                if (!checked) setMetodoSel(null);
                            }}
                        />
                    </div>

                    <div className="col-md-6">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Fecha de venta</div>
                        <DatePicker
                            selected={fecha}
                            onChange={(d) => setFecha(d)}
                            className="form-control"
                            placeholderText="Fecha"
                            dateFormat="dd/MM/yyyy"
                        />
                    </div>

                    <div className="col-md-2">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>Moneda</div>
                        <Form.Select
                            value={moneda}
                            onChange={(e) => setMoneda(Number(e.target.value))}
                        >
                            <option value={0}>MXN</option>
                            <option value={1}>USD</option>
                        </Form.Select>
                    </div>

                    <div className="col-md-2">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>T. Cambio</div>
                        <Form.Control
                            type="number"
                            min="0"
                            value={Number(moneda) === 1 ? tipoCambioUsd : '1'}
                            onChange={(e) => setTipoCambioUsd(e.target.value)}
                            disabled={Number(moneda) !== 1}
                        />
                    </div>
                </div>

                {!esCobradaInmediato && (
                    <div className="alert alert-warning" style={{ fontSize: 13 }}>
                        Venta a crédito: se sumará al saldo pendiente del cliente en su cuenta.
                    </div>
                )}

                <div className="table-responsive">
                    <Table bordered hover className="mb-0">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th style={{ width: 110 }}>Código</th>
                                <th style={{ width: 90 }} className="text-center">Cantidad</th>
                                <th style={{ width: 120 }} className="text-end">Precio</th>
                                <th style={{ width: 260 }}>Ubicación</th>
                                <th style={{ width: 120 }} className="text-end">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listado.map((it) => {
                                const estado = estadoPorProducto[it.key] || { ubicaciones: [], completo: false, sinStock: true, asignado: 0 };
                                const cantidad = Number(it.cantidad || 0);
                                const subtotal = Number(it.precio || 0) * cantidad * (it.iva ? 1.16 : 1);
                                const asign = asignaciones[it.key] || {};
                                const conFlicto = !estado.completo || estado.sinStock;

                                return (
                                    <tr key={it.key} className={conFlicto ? 'table-danger' : ''}>
                                        <td className="align-middle">{it.productoNombre}</td>
                                        <td className="align-middle">{it.codigoTienda || '-'}</td>
                                        <td className="text-center align-middle">{formatNumber(cantidad)}</td>
                                        <td className="text-end align-middle">{prefMoneda} {formatMoney(it.precio)}</td>
                                        <td>
                                            {it.sinInventario ? (
                                                <div className="text-muted" style={{ fontSize: 12 }}>
                                                    Sin control de inventario
                                                </div>
                                            ) : estado.sinStock ? (
                                                <div className="text-danger" style={{ fontSize: 12 }}>
                                                    Sin stock suficiente para convertir. Ajusta la cotización o espera inventario.
                                                </div>
                                            ) : (
                                                <div>
                                                    {estado.ubicaciones.map((u) => {
                                                        const key = buildUbicKey(u);
                                                        const restante = disponibleRestante(it.productoId, u, asign[key] || 0);
                                                        return (
                                                            <div key={key} className="d-flex align-items-center gap-2 mb-1">
                                                                <div style={{ minWidth: 130, fontSize: 12 }}>{u.label}</div>
                                                                <Form.Control
                                                                    type="number"
                                                                    size="sm"
                                                                    value={asign[key] ?? 0}
                                                                    min={0}
                                                                    max={restante}
                                                                    style={{ width: 90 }}
                                                                    onChange={(e) => onChangeAsignacion(it.key, it.productoId, u, e.target.value)}
                                                                />
                                                                <div className="text-muted" style={{ fontSize: 11 }}>
                                                                    / {formatNumber(restante)}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    <div className={estado.completo ? 'text-success' : 'text-danger'} style={{ fontSize: 11 }}>
                                                        Asignado: {formatNumber(estado.asignado)} / {formatNumber(cantidad)}
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-end align-middle">{prefMoneda} {formatMoney(subtotal)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>

                {listadoPaquetes.length > 0 && (
                    <div className="table-responsive mt-3">
                        <Table bordered hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>Paquete</th>
                                    <th style={{ width: 90 }} className="text-center">Cantidad</th>
                                    <th style={{ width: 120 }} className="text-end">Precio</th>
                                    <th>Componentes y ubicación</th>
                                    <th style={{ width: 120 }} className="text-end">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listadoPaquetes.map((p) => {
                                    const estado = estadoPorPaquete[p.key] || { detalleComponentes: [], completo: false };
                                    const cantidad = Number(p.cantidad || 0);
                                    const subtotal = Number(p.precio || 0) * cantidad * (p.iva ? 1.16 : 1);
                                    const conFlicto = !estado.completo;

                                    return (
                                        <tr key={p.key} className={conFlicto ? 'table-danger' : ''}>
                                            <td className="align-middle">
                                                <div className="fw-semibold">{p.paqueteNombre}</div>
                                                {p.paqueteCodigo ? (
                                                    <div className="text-muted" style={{ fontSize: 12 }}>{p.paqueteCodigo}</div>
                                                ) : null}
                                            </td>
                                            <td className="text-center align-middle">{formatNumber(cantidad)}</td>
                                            <td className="text-end align-middle">{prefMoneda} {formatMoney(p.precio)}</td>
                                            <td>
                                                {estado.detalleComponentes.map((d) => (
                                                    <div key={d.productoId} className="mb-2">
                                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                                            {d.productoNombre} — necesario: {formatNumber(d.necesaria)}
                                                        </div>

                                                        {d.ubicaciones.length === 0 ? (
                                                            <div className="text-danger" style={{ fontSize: 12 }}>Sin existencia</div>
                                                        ) : (
                                                            <>
                                                                {d.ubicaciones.map((u) => {
                                                                    const ubicKey = buildUbicKey(u);
                                                                    const restante = disponibleRestante(d.productoId, u, d.asign[ubicKey] || 0);
                                                                    return (
                                                                        <div key={ubicKey} className="d-flex align-items-center gap-2 mb-1">
                                                                            <div style={{ minWidth: 150, fontSize: 12 }}>{u.label}</div>
                                                                            <div className="text-muted" style={{ fontSize: 11, minWidth: 70 }}>
                                                                                Disp: {formatNumber(restante)}
                                                                            </div>
                                                                            <Form.Control
                                                                                type="number"
                                                                                size="sm"
                                                                                value={d.asign[ubicKey] ?? 0}
                                                                                min={0}
                                                                                max={restante}
                                                                                style={{ width: 90 }}
                                                                                onChange={(e) => onChangeAsignacionPaquete(p.key, d.productoId, u, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                                <div className={d.completo ? 'text-success' : 'text-danger'} style={{ fontSize: 11 }}>
                                                                    Asignado: {formatNumber(d.asignado)} / {formatNumber(d.necesaria)}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                            </td>
                                            <td className="text-end align-middle">{prefMoneda} {formatMoney(subtotal)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-3">
                    <Badge bg="success">Total de la venta: {prefMoneda} {formatMoney(total)}</Badge>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirm} disabled={!puedeConfirmar || loading}>
                    {loading ? 'Convirtiendo...' : 'Confirmar conversión'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
